/**
 * Utility function to determine readable text color based on background color
 * Used by theme system to ensure text is readable on various backgrounds
 */

// @ts-nocheck
export function getReadableTextColorAA(
  bg: string,
  whiteColor = "#FFFFFF",
  blackColor = "#000000",
  requiredRatio = 4.5, // AA for normal text; use 3.0 for large text if needed
): string {
  const toRGB = (c: string): [number, number, number] => {
    if (c.startsWith("#")) {
      const h = c.slice(1)
      const hex =
        h.length === 3
          ? h
              .split("")
              .map((x) => x + x)
              .join("")
          : h
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return [r, g, b]
    }
    if (c.startsWith("rgb")) {
      const m = c.match(/\d+/g)
      if (!m || m.length < 3) throw new Error(`Invalid rgb color: ${c}`)
      return [Number(m[0]), Number(m[1]), Number(m[2])]
    }
    throw new Error(`Unsupported color format: ${c}`)
  }

  const relLum = (hex: string): number => {
    const rgb = toRGB(hex)
      .map((v) => v / 255)
      .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4))
    return 0.2126 * rgb[0]! + 0.7152 * rgb[1]! + 0.0722 * rgb[2]!
  }

  const contrast = (a: string, b: string): number => {
    const lums = [relLum(a), relLum(b)].sort((x, y) => y - x)
    return (lums[0]! + 0.05) / (lums[1]! + 0.05)
  }

  const crWhite = contrast(whiteColor, bg)
  const crBlack = contrast(blackColor, bg)

  // Prefer any that meets AA; if both meet, pick the higher ratio; if neither meets, pick the higher ratio anyway.
  const wPass = crWhite >= requiredRatio
  const bPass = crBlack >= requiredRatio

  if (wPass && bPass) return crWhite >= crBlack ? whiteColor : blackColor
  if (wPass) return whiteColor
  if (bPass) return blackColor
  return crWhite >= crBlack ? whiteColor : blackColor
}

export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const s = hex.replace("#", "")
  const n = parseInt(s, 16)
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  }
}

export const withAlpha = (hex: string, a: number): string => {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${a}%)`
}

export const onColor = (bg: string): string => {
  // AA for normal text
  const lum = (h: string): number => {
    const rgb = hexToRgb(h)
    const vals = [rgb.r, rgb.g, rgb.b]
      .map((v) => v / 255)
      .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4))
    return 0.2126 * vals[0]! + 0.7152 * vals[1]! + 0.0722 * vals[2]!
  }
  const cr = (a: string, b: string): number => {
    const lums = [lum(a), lum(b)].sort((x, y) => y - x)
    return (lums[0]! + 0.05) / (lums[1]! + 0.05)
  }
  return cr("#000000", bg) >= 4.5 ? "#000000" : "#FFFFFF"
}

export async function dominantColorFromImage(src: string, downscale = 64) {
  const img = await loadImage(src)
  const { width, height } = scaledSize(img.naturalWidth, img.naturalHeight, downscale)
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!
  ctx.drawImage(img, 0, 0, width, height)
  const { data } = ctx.getImageData(0, 0, width, height)

  // 4-bit/channel quantization for robustness
  const counts = new Map<number, number>()
  let maxKey = 0,
    maxCount = -1
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 16) continue // ignore near-transparent
    const key = ((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4)
    const c = (counts.get(key) || 0) + 1
    counts.set(key, c)
    if (c > maxCount) {
      maxCount = c
      maxKey = key
    }
  }
  const rb = (maxKey >> 8) & 0xf,
    gb = (maxKey >> 4) & 0xf,
    bb = maxKey & 0xf
  const r = (rb << 4) + 8,
    g = (gb << 4) + 8,
    b = (bb << 4) + 8
  return { r, g, b, hex: toHex(r, g, b), css: `rgb(${r} ${g} ${b})` }
}

function scaledSize(w: number, h: number, targetMax: number) {
  if (w <= targetMax && h <= targetMax) return { width: w, height: h }
  const s = Math.max(w, h) / targetMax
  return {
    width: Math.max(1, Math.round(w / s)),
    height: Math.max(1, Math.round(h / s)),
  }
}

function toHex(n: number, g?: number, b?: number) {
  const h = (x: number) => x.toString(16).padStart(2, "0")
  return `#${h(n)}${h(g!)}${h(b!)}`
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous" // requires CORS headers if cross-origin
    img.decoding = "async"
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}
